import React, {memo, useEffect, useRef} from 'react';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {StaticImageData} from 'next/image';
import MUIButton from '@/components/MUI-Components/MUI-Button';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import DisplayImage from '../display-image';
import {reviewBadgeCards, reviewBadgeLinks, reviewBadgeLogoIntrinsic} from '@/config/reviewLinks';
import ReviewStarRating from '@/components/Common/ReviewStarRating';
import {betweenWidth, maxWidth, minWidth} from '@/styles/breakpoints';
import {borderRadius, colors, fontSize, fontWeight, gradients, letterSpacing, lineHeight, overlay, spacing, zIndex} from '@/styles/tokens';
import {nftButtonSx} from '@/styles/nftButtonSx';

// The split hero's copy lives in a ~620px column beside the artwork, the same shape as
// the home banner's narrow column — so it borrows the home banner's step-down below
// 1650px, where the wide-screen sizes wrap the heading and subheading too often. The
// badge cards step down at the same breakpoint, so they stay in proportion with it.
const SPLIT_NARROW = maxWidth(1650);

// Module-level sx constants
// Matches MUI's `sm` breakpoint (600px): everything below it.
const MOBILE_MEDIA_QUERY = '@media (max-width: 599.95px)';

const bannerBoxSx = {
    minHeight: {xs: '60vh', sm: '80vh', md: 'calc(110vh - 130px)'},
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: {xs: 'flex-start', sm: 'center'},
    textAlign: {xs: 'left', sm: 'center'},
    overflow: 'hidden',
    background: {
        xs: gradients.darkSection,
        sm: colors.primary.videoBg
    }
} as const;

const overlayGradientSx = {
    display: {xs: 'none', sm: 'block'},
    position: 'absolute',
    inset: 0,
    zIndex: 0.5,
    background: colors.primary.videoBg,
    pointerEvents: 'none',
    opacity: 0.7
} as const;

// Split layout: one even, light wash over the whole photograph — enough for the white
// copy to read without darkening the artwork on the right. Phones show the plain mobile
// gradient instead of the photo, so the wash starts at `sm`.
const SPLIT_WASH_OPACITY = 0.35;
const splitOverlayGradientSx = {
    display: {xs: 'none', sm: 'block'},
    position: 'absolute',
    inset: 0,
    zIndex: 0.5,
    background: colors.primary.deep,
    opacity: SPLIT_WASH_OPACITY,
    pointerEvents: 'none'
} as const;

const containerSx = {position: 'relative', zIndex: zIndex.raised} as const;

// The split layout's badge row is pinned to the bottom of this container, so the
// container has to fill the banner's height rather than shrink-wrap the copy —
// otherwise the cards float wherever the copy happens to end, which is well above
// the banner's bottom edge below 1650px, where the heading steps down a size.
const splitContainerSx = {
    ...containerSx,
    alignSelf: 'stretch',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
} as const;

// ─── Split layout ────────────────────────────────────────────────────────────
// The copy occupies the left half and stays left-aligned at every width; the
// background photograph is left uncovered on the right.
const splitBannerBoxSx = {
    ...bannerBoxSx,
    minHeight: {xs: '60vh', sm: '70vh', md: 'calc(100vh - 130px)'},
    justifyContent: 'flex-start',
    textAlign: 'left',
    // The badge row hangs below the copy column, so the banner carries its own vertical
    // padding to keep the cards clear of its edges (it clips its overflow). On a laptop
    // the whole hero — copy, CTA and badges — has to land inside one viewport, so the
    // padding only opens up on tall screens.
    // Phones take no banner padding: the copy column's own pt/pb sets the spacing, as
    // on the home banner.
    py: {xs: 0, sm: 7, xl: 10},
    '@media (min-width: 900px) and (max-height: 860px)': {py: 5}
} as const;

const splitContentBoxSx = {
    // Same widths as the home banner's copy column (`home-banner` `contentBoxSx`).
    maxWidth: {xs: '100%', md: 760},
    '@media (max-width: 1499px)': {maxWidth: {xs: '100%', md: '510px'}},
    [betweenWidth(1500, 1800)]: {maxWidth: '544px'},
    // Flex items default to `min-width: auto`, which would let the column refuse to
    // shrink below its content's min-content size on small screens.
    minWidth: 0,
    mx: 0,
    pt: {xs: 8, sm: 6, md: 3},
    pb: {xs: 6, sm: 6, md: 3},
    '@media (min-width: 900px) and (max-height: 860px)': {pt: 2, pb: 2}
} as const;

// Opt-in fit for wide screens (`splitImageAspectRatio`). Above 1500px a `cover` background
// on a viewport-tall banner zooms the artwork, pushing it off the right edge and over the
// copy. Capping the banner's min-height to the image's own proportions keeps it unzoomed,
// so the copy column can widen into the space the artwork leaves on the left.
const SPLIT_WIDE = minWidth(1500);

const splitBannerFitSx = (aspectRatio: number) => ({
    ...splitBannerBoxSx,
    [SPLIT_WIDE]: {minHeight: `min(calc(100vh - 130px), calc(100vw / ${aspectRatio}))`}
});

// The artwork's empty left side is ~45vw wide, but the container stops growing at 1536px,
// so the room left for copy shrinks as the screen widens: roughly 720px - 5vw.
const splitContentFitSx = (wideMaxWidth = 'min(640px, calc(720px - 5vw))') => ({
    ...splitContentBoxSx,
    [SPLIT_WIDE]: {maxWidth: wideMaxWidth}
});

// Badges sit over the photograph at the bottom right on desktop, as three separate
// cards rather than the centred block with its own heading. Below `md` there is no room
// beside the copy, so they fall back into the flow under the buttons.
const splitBadgesRowSx = {
    display: {xs: 'none', sm: 'flex'},
    flexDirection: 'row',
    alignItems: 'stretch',
    justifyContent: {sm: 'flex-start', md: 'flex-end'},
    gap: {sm: 1, md: 1.25},
    [SPLIT_NARROW]: {gap: 1},
    mt: {sm: 5, md: 0},
    position: {md: 'absolute'},
    // The floating WhatsApp button and chat launcher own the rightmost ~72px of the
    // viewport, and they are fixed — so at some scroll position they sit over anything
    // in that band. The row is therefore kept clear of it: 88px from the viewport's
    // right edge, expressed as an offset from the container's own right edge
    // (`(100vw - 100%) / 2` is the container's centring margin, zero when it runs
    // full-bleed). It relaxes back to 24px once the container is inset far enough
    // on its own, on wide screens.
    right: {md: 'max(24px, calc(88px - (100vw - 100%) / 2))'},
    // Pinned to the bottom of the (full-height) container, so the cards sit on the
    // banner's bottom edge at every width instead of tracking the end of the copy.
    bottom: {md: 0},
    zIndex: zIndex.raised
} as const;

// Opt-in alternative: the cards stay in the copy column's flow, left-aligned directly
// under the buttons, instead of floating over the artwork at the bottom right.
const splitBadgesInlineRowSx = {
    ...splitBadgesRowSx,
    justifyContent: 'flex-start',
    mt: {sm: 5, md: 4},
    '@media (min-width: 900px) and (max-height: 860px)': {mt: 3},
    position: 'static',
    right: 'auto',
    bottom: 'auto'
} as const;

// Each badge becomes a translucent dark card stacking the white wordmark over the
// score, its stars and the review count — the rating is real text, so it stays
// crawlable and can be kept in step with the AggregateRating schema.
const splitBadgeCardSx = {
    flex: '0 1 auto',
    width: {sm: 150, md: 164, xl: 176},
    [SPLIT_NARROW]: {width: 132, px: 1, py: 1.25, gap: 0.5},
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0.75,
    px: 1.5,
    py: {xs: 2, md: 1.75, xl: 2},
    borderRadius: borderRadius.xl,
    background: gradients.heroBadgeCardTranslucent,
    border: `1px solid ${overlay.whiteMid}`,
    backdropFilter: 'blur(8px)',
    textDecoration: 'none',
    transition: 'transform 0.25s ease, border-color 0.25s ease',
    '&:hover': {transform: 'translateY(-3px)', borderColor: overlay.whiteBorder}
} as const;

// All three logos sit in one fixed-height slot, sized to the tallest of them, so the
// rating rows below line up across the three cards.
const splitBadgeLogoSlotSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 48,
    // The logo height is an inline style (it varies per badge), so the narrow-layout
    // step-down has to outrank it; it reads back the per-badge value from the
    // `--logo-h-narrow` custom property set alongside it.
    [SPLIT_NARROW]: {height: 38, '& img': {height: 'var(--logo-h-narrow) !important'}}
} as const;

const splitBadgeLogoStyle = (height: number): React.CSSProperties => ({
    width: 'auto',
    height: `${height}px`,
    maxWidth: '100%',
    objectFit: 'contain',
    objectPosition: 'center',
    // Roughly four-fifths of the wide-screen height, matching the card's own step-down.
    ['--logo-h-narrow' as string]: `${Math.round(height * 0.8)}px`
});

const splitBadgeRatingRowSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0.75,
    [SPLIT_NARROW]: {gap: 0.5}
} as const;

const splitBadgeRatingSx = {
    fontSize: fontSize[16],
    lineHeight: 1,
    fontWeight: fontWeight.bold,
    color: colors.text.inverse,
    [SPLIT_NARROW]: {fontSize: fontSize[14]}
} as const;

const splitBadgeNoteSx = {
    fontSize: fontSize[12],
    lineHeight: 1,
    color: overlay.whiteDim,
    textAlign: 'center' as const,
    [SPLIT_NARROW]: {fontSize: fontSize[10]}
} as const;

const splitButtonRowSx = {
    display: 'flex',
    flexDirection: {xs: 'column', sm: 'row'},
    gap: 2,
    justifyContent: 'flex-start',
    // Stretched on phones so the CTA runs the full width, as on the home banner.
    alignItems: {xs: 'stretch', sm: 'center'}
} as const;

const splitPrimaryButtonSx = {...nftButtonSx, width: {xs: '100%', sm: 'auto'}, height: 42} as const;

const desktopVideoStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center',
    zIndex: zIndex.base
};

const badgesRowSx = {
    display: {xs: 'none', sm: 'flex'},
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    // Tighter gap below desktop so the three badges keep more of their own width.
    gap: {sm: 2, md: 4},
    mt: 3
} as const;

/**
 * Each badge is a shrinkable flex item. `minWidth: 0` is the important part — without it a
 * flex item refuses to go below its content width, which is what pushed the row past the
 * container and clipped the last badge between 600px and 943px.
 */
const badgeLinkSx = {
    display: 'block',
    flex: '1 1 0',
    minWidth: 0,
    maxWidth: 271
} as const;

const dividerSx = {
    width: '1px',
    // Shorter below desktop, where the badges themselves shrink — a fixed 78px would
    // overhang them. Desktop keeps the original height.
    height: {sm: '56px', md: '78px'},
    background: gradients.badgeDivider,
    flexShrink: 0
} as const;

// Split layout on phones: the home banner's stacked badge list — one row per platform,
// logo on the left, score and note on the right, hairlines between rows.
const splitMobileBadgesSx = {
    display: {xs: 'flex', sm: 'none'},
    flexDirection: 'column',
    gap: 1,
    width: '100%',
    mt: 3,
    px: 1.25,
    py: 1,
    borderRadius: borderRadius.lg
} as const;

const splitMobileBadgeRowSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    textDecoration: 'none',
    '& + &': {borderTop: `1px solid ${overlay.whiteFaint}`, pt: 0.75}
} as const;

const splitMobileBadgeLogoSlotSx = {display: 'flex', alignItems: 'center', height: 54} as const;

const splitMobileBadgeMetaSx = {display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5} as const;

const hiddenSpanSx: React.CSSProperties = {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: '0',
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: '0'
};

// ─── New sx constants ─────────────────────────────────────────────────────────
const pageBadgeSx = {
    display: 'inline-block',
    px: spacing[16],
    py: spacing[8],
    mb: spacing[20],
    border: `1px solid ${overlay.whiteMid}`,
    borderRadius: borderRadius.full,
    bgcolor: overlay.whiteSubtle,
    color: colors.text.inverse,
    fontSize: fontSize[14],
    fontWeight: fontWeight.medium,
    letterSpacing: letterSpacing.em,
    backdropFilter: 'blur(4px)'
} as const;

const featurePillsWrapSx = {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: spacing[8],
    justifyContent: {xs: 'flex-start', sm: 'center'},
    mt: spacing[24],
    mb: spacing[28]
} as const;

const pillItemSx = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: spacing[8],
    bgcolor: colors.primary.buttonHover,
    borderRadius: borderRadius.md,
    px: spacing[12],
    height: spacing[40],
    color: colors.text.inverse,
    whiteSpace: 'nowrap' as const
} as const;

const pillCheckIconSx = {
    color: colors.status.success,
    fontSize: fontSize[16]
} as const;

const pillTextSx = {
    fontSize: fontSize[14],
    fontWeight: fontWeight.regular,
    color: 'inherit',
    lineHeight: 1
} as const;

// Hero <h1> size ladder — steps up on its own as the viewport widens:
//   < 600px       35px / 45px   phones
//   600–1199px    38px / 48px   large phones + tablets
//   1200–1450px   40px / 50px   laptops
//   >= 1451px     45px / 55px   wide desktop
// MUI's default breakpoints cover 600 (`sm`) and 1200 (`lg`); the widest step is a
// bespoke width so it is written as a raw media query. Was `.home-page-banner-heading`
// in globals.scss (removed — every hero now owns this as `sx`).
const headingSx = {
    fontWeight: fontWeight.bold,
    color: colors.text.inverse,
    fontSize: {xs: '35px', sm: '38px', lg: '40px'},
    lineHeight: {xs: '45px', sm: '48px', lg: '50px'},
    '@media (min-width: 1451px)': {
        fontSize: '45px',
        lineHeight: '55px'
    }
} as const;

// Key order matters: the 1451px step is declared last so it still wins in the
// 1451–1650 band, exactly as it does on the home banner.
const splitHeadingSx = {
    fontWeight: fontWeight.bold,
    color: colors.text.inverse,
    fontSize: {xs: '35px', sm: '38px', lg: '40px'},
    lineHeight: {xs: '45px', sm: '48px', lg: '50px'},
    [SPLIT_NARROW]: {fontSize: '36px', lineHeight: '46px'},
    '@media (min-width: 1451px)': {fontSize: '45px', lineHeight: '55px'}
} as const;

const subheadingSx = {
    color: `${colors.text.inverse} !important`,
    '& a': {
        color: colors.text.inverse,
        textDecoration: 'underline',
        '&:hover': {
            color: colors.accent.orange
        }
    }
} as const;

const splitSubheadingSx = {
    ...subheadingSx,
    mb: spacing[32],
    fontSize: fontSize[16],
    lineHeight: lineHeight[24],
    '@media (min-width: 769px)': {lineHeight: lineHeight[28]},
    [SPLIT_NARROW]: {fontSize: fontSize[14], lineHeight: lineHeight[24]}
} as const;

const desktopBadgesHeadingWrapSx = {display: {xs: 'none', sm: 'block'}, mt: 8, mb: 1} as const;

const desktopBadgesHeadingSx = {
    color: colors.text.inverse,
    fontSize: fontSize[20],
    fontWeight: fontWeight.semibold
} as const;

const desktopBadgesRowSx = {...badgesRowSx, mt: 3} as const;

const mobileBadgesWrapSx = {
    display: {xs: 'flex', sm: 'none'},
    flexDirection: 'column',
    alignItems: {xs: 'flex-start', sm: 'center'},
    gap: {xs: 3, md: 8},
    mt: {xs: 4, md: 6}
} as const;

const mobileBadgesHeadingSx = {
    color: colors.text.inverse,
    textAlign: {xs: 'left', sm: 'center'},
    fontSize: fontSize[20],
    fontWeight: fontWeight.semibold
} as const;

interface HeroVideoBannerProps {
    heading: string;
    subheading: string;
    primaryButtonText: string;
    secondaryButtonText?: string;
    onPrimaryClick: () => void;
    onSecondaryClick?: () => void;
    backgroundVideo?: string;
    posterImage?: string;
    videoAlt?: string;
    backgroundImage?: string | StaticImageData;
    /** Drop `backgroundImage` below 600px and fall back to the plain dark gradient. */
    hideBackgroundImageOnMobile?: boolean;
    /**
     * `centered` (default) keeps the copy centred over the full-bleed background.
     * `split` left-aligns the copy in the first half of the banner and clears the
     * overlay towards the right, so the artwork in a background photograph stays
     * visible beside it.
     */
    layout?: 'centered' | 'split';
    /**
     * Split layout only. `right` (default) pins the badge cards to the banner's bottom
     * right over the artwork; `below-content` left-aligns them under the buttons.
     */
    splitBadgesPlacement?: 'right' | 'below-content';
    /**
     * Split layout only. Set false to drop the light blue wash over the photograph, for
     * artwork whose copy side is already dark enough for white text.
     */
    splitImageWash?: boolean;
    /**
     * Split layout only. The background image's width / height. When set, screens from
     * 1500px cap the banner's height to the image's proportions (no `cover` zoom) and
     * widen the copy column to fill the space beside the artwork.
     */
    splitImageAspectRatio?: number;
    /**
     * CSS `background-position` for `backgroundImage`. Defaults to `center`. Use
     * `right center` for wide artwork whose subject sits on the right, so `cover`
     * trims the empty left side instead of cutting the subject off the right edge.
     */
    backgroundPosition?: string;
    /**
     * Split layout with `splitImageAspectRatio` only. Copy column max-width from 1500px.
     * The default suits artwork whose subject starts near the middle; wider artwork with
     * more empty space on the left can afford a wider column.
     */
    splitWideContentMaxWidth?: string;
    /** Small pill label rendered above the heading, e.g. "Custom Software Development Company" */
    pageBadge?: string;
    /** Checkmark feature list shown between subheading and CTA buttons */
    featurePills?: string[];
    /**
     * Icon rendered before the secondary button label (e.g. a phone icon on a
     * "Talk to Our Expert Now" CTA). When passed, the secondary button drops its
     * trailing arrow so the icon is the only affordance.
     */
    secondaryButtonIcon?: React.ReactNode;
}

const HeroVideoBanner = (props: HeroVideoBannerProps) => {
    const {
        heading,
        subheading,
        primaryButtonText,
        secondaryButtonText,
        onPrimaryClick,
        onSecondaryClick,
        backgroundVideo,
        posterImage,
        videoAlt,
        backgroundImage,
        hideBackgroundImageOnMobile = false,
        layout = 'centered',
        splitBadgesPlacement = 'right',
        splitImageWash = true,
        splitImageAspectRatio,
        backgroundPosition = 'center',
        splitWideContentMaxWidth,
        pageBadge,
        featurePills,
        secondaryButtonIcon
    } = props;

    const isSplit = layout === 'split';

    const desktopVideoRef = useRef<HTMLVideoElement>(null);
    useEffect(() => {
        const loadAndPlayVideo = (videoElement: HTMLVideoElement | null) => {
            if (videoElement) {
                const source = videoElement.querySelector('source');
                if (source && source.dataset.src) {
                    source.src = source.dataset.src;
                    videoElement.load();
                    videoElement.play().catch(() => {
                        // Ignore autoplay policy errors
                    });
                }
            }
        };

        const timer = setTimeout(() => {
            loadAndPlayVideo(desktopVideoRef.current);
        }, 1000);

        return () => clearTimeout(timer);
    }, []);

    const badgeStyle: React.CSSProperties = {objectFit: 'contain', width: '100%', height: 'auto'};

    // Desktop badges with gradient dividers between them
    const desktopBadges = (
        <>
            {reviewBadgeLinks.map((badge, index) => (
                <React.Fragment key={badge.key}>
                    {index > 0 && <Box sx={dividerSx} />}
                    <Box component="a" href={badge.href} target="_blank" rel="noopener noreferrer" sx={badgeLinkSx}>
                        <DisplayImage src={badge.src} alt={badge.alt} width={271} height={65} style={badgeStyle} />
                        <span style={hiddenSpanSx}>{badge.hiddenLabel}</span>
                    </Box>
                </React.Fragment>
            ))}
        </>
    );

    // Split layout: each badge in its own card, logo over rating + stars + note.
    const splitBadges = (
        <>
            {reviewBadgeCards.map((badge) => (
                <Box key={badge.key} component="a" href={badge.href} target="_blank" rel="noopener noreferrer" sx={splitBadgeCardSx}>
                    <Box sx={splitBadgeLogoSlotSx}>
                        <DisplayImage
                            src={badge.logo}
                            alt={badge.alt}
                            {...reviewBadgeLogoIntrinsic}
                            style={splitBadgeLogoStyle(badge.logoHeight)}
                        />
                    </Box>
                    <Box sx={splitBadgeRatingRowSx}>
                        <Typography component="span" className="font-headers" sx={splitBadgeRatingSx}>
                            {badge.rating.toFixed(1)}
                        </Typography>
                        <ReviewStarRating rating={badge.rating} color={badge.starColor} />
                    </Box>
                    <Typography component="span" className="font-regular-text" sx={splitBadgeNoteSx}>
                        {badge.note}
                    </Typography>
                    <span style={hiddenSpanSx}>{badge.srLabel}</span>
                </Box>
            ))}
        </>
    );

    const splitMobileBadges = (
        <Box sx={splitMobileBadgesSx}>
            {reviewBadgeCards.map((badge) => (
                <Box key={badge.key} component="a" href={badge.href} target="_blank" rel="noopener noreferrer" sx={splitMobileBadgeRowSx}>
                    <Box sx={splitMobileBadgeLogoSlotSx}>
                        <DisplayImage
                            src={badge.logo}
                            alt={badge.alt}
                            {...reviewBadgeLogoIntrinsic}
                            style={splitBadgeLogoStyle(badge.logoHeight)}
                        />
                    </Box>
                    <Box sx={splitMobileBadgeMetaSx}>
                        <Box sx={splitBadgeRatingRowSx}>
                            <Typography component="span" className="font-headers" sx={splitBadgeRatingSx}>
                                {badge.rating.toFixed(1)}
                            </Typography>
                            <ReviewStarRating rating={badge.rating} color={badge.starColor} />
                        </Box>
                        <Typography component="span" className="font-regular-text" sx={splitBadgeNoteSx}>
                            {badge.note}
                        </Typography>
                    </Box>
                    <span style={hiddenSpanSx}>{badge.srLabel}</span>
                </Box>
            ))}
        </Box>
    );

    // Mobile badges — no dividers
    const mobileBadgeStyle: React.CSSProperties = {objectFit: 'contain', width: '250px', height: '62px', margin: `${spacing[8]} 0`};

    const mobileBadges = (
        <>
            {reviewBadgeLinks.map((badge) => (
                <a key={badge.key} href={badge.href} target="_blank" rel="noopener noreferrer">
                    <DisplayImage src={badge.src} alt={badge.alt} width={250} height={62} style={mobileBadgeStyle} />
                    <span style={hiddenSpanSx}>{badge.hiddenLabel}</span>
                </a>
            ))}
        </>
    );

    return (
        <>
            {/* Desktop and Mobile Banner with video and content */}
            <Box
                sx={{
                    ...(isSplit ? (splitImageAspectRatio ? splitBannerFitSx(splitImageAspectRatio) : splitBannerBoxSx) : bannerBoxSx),
                    // The split artwork already carries its own dark wash on the copy side,
                    // so it is used straight; the centred layout still needs the overlay to
                    // keep text legible over the middle of a photograph.
                    background: backgroundImage
                        ? `${isSplit ? '' : `${gradients.darkSectionOverlay}, `}url("${
                              typeof backgroundImage === 'string' ? encodeURI(backgroundImage) : backgroundImage.src
                          }")`
                        : bannerBoxSx.background,
                    backgroundSize: 'cover',
                    backgroundPosition,
                    ...(backgroundImage &&
                        hideBackgroundImageOnMobile && {
                            [MOBILE_MEDIA_QUERY]: {background: isSplit ? gradients.heroBannerMobile : gradients.darkSection}
                        })
                }}
            >
                {backgroundVideo && (
                    <video
                        id="hero-video"
                        ref={desktopVideoRef}
                        loop
                        muted
                        playsInline
                        preload="none"
                        poster={posterImage}
                        className="hero-video-desktop"
                        style={desktopVideoStyle}
                        aria-label={videoAlt}
                    >
                        <source data-src={backgroundVideo} media="(min-width: 600px)" type="video/mp4" />
                    </video>
                )}

                {backgroundVideo && <Box sx={overlayGradientSx} />}
                {isSplit && backgroundImage && splitImageWash && <Box sx={splitOverlayGradientSx} />}

                <MUIContainer sx={isSplit ? splitContainerSx : containerSx}>
                    <Box
                        sx={
                            isSplit
                                ? splitImageAspectRatio
                                    ? splitContentFitSx(splitWideContentMaxWidth)
                                    : splitContentBoxSx
                                : {maxWidth: 950, mx: {xs: 0, sm: 'auto'}, pt: {xs: 8, sm: 5}, pb: {xs: 6, sm: 5}}
                        }
                    >
                        {pageBadge && (
                            <Box textAlign={isSplit ? 'left' : {xs: 'left', sm: 'center'}}>
                                <Box component="span" sx={pageBadgeSx}>
                                    {pageBadge}
                                </Box>
                            </Box>
                        )}

                        <Typography variant="h1" component="h1" gutterBottom sx={isSplit ? splitHeadingSx : headingSx}>
                            {heading}
                        </Typography>

                        <Typography
                            variant="subtitle1"
                            component="p"
                            className={isSplit ? 'font-regular-text' : 'home-page-banner-subheading'}
                            sx={isSplit ? splitSubheadingSx : subheadingSx}
                            dangerouslySetInnerHTML={{__html: subheading}}
                        />

                        {featurePills && featurePills.length > 0 && (
                            <Box sx={featurePillsWrapSx}>
                                {featurePills.map((pill) => (
                                    <Box key={pill} sx={pillItemSx}>
                                        <CheckIcon sx={pillCheckIconSx} />
                                        <Typography component="span" className="font-regular-text" sx={pillTextSx}>
                                            {pill}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}

                        <Box
                            sx={
                                isSplit
                                    ? splitButtonRowSx
                                    : {
                                          display: 'flex',
                                          flexDirection: {xs: 'column', sm: 'row'},
                                          gap: 2,
                                          justifyContent: {xs: 'flex-start', sm: 'center'},
                                          alignItems: {xs: 'flex-start', sm: 'center'}
                                      }
                            }
                        >
                            <MUIButton
                                label={primaryButtonText}
                                aria-label={primaryButtonText}
                                variant="contained"
                                size="medium"
                                endIcon={<ArrowForwardIcon />}
                                disableRipple={true}
                                className="animated-button moving-icon-button"
                                sx={isSplit ? splitPrimaryButtonSx : nftButtonSx}
                                onClick={onPrimaryClick}
                            />
                            {secondaryButtonText && (
                                <MUIButton
                                    label={secondaryButtonText}
                                    aria-label={secondaryButtonText}
                                    variant="outlined"
                                    size="medium"
                                    startIcon={secondaryButtonIcon}
                                    endIcon={secondaryButtonIcon ? undefined : <ArrowForwardIcon />}
                                    disableRipple={true}
                                    className="moving-icon-button animated-button transparent-button"
                                    onClick={onSecondaryClick}
                                />
                            )}
                        </Box>

                        {/* Badges row — desktop only */}
                        {!isSplit && (
                            <Box sx={desktopBadgesHeadingWrapSx}>
                                <Typography className="font-headers" component="p" sx={desktopBadgesHeadingSx}>
                                    RECOGNIZED BY LEADING REVIEW PLATFORMS
                                </Typography>
                            </Box>
                        )}
                        <Box
                            sx={
                                isSplit
                                    ? splitBadgesPlacement === 'below-content'
                                        ? splitBadgesInlineRowSx
                                        : splitBadgesRowSx
                                    : desktopBadgesRowSx
                            }
                        >
                            {isSplit ? splitBadges : desktopBadges}
                        </Box>

                        {/* Badges row — mobile only */}
                        {isSplit ? (
                            splitMobileBadges
                        ) : (
                            <Box sx={mobileBadgesWrapSx}>
                                <Typography component="p" className="font-headers" sx={mobileBadgesHeadingSx}>
                                    RECOGNIZED BY LEADING B2B RATING & REVIEW PLATFORMS
                                </Typography>
                                {mobileBadges}
                            </Box>
                        )}
                    </Box>
                </MUIContainer>
            </Box>
        </>
    );
};

export default memo(HeroVideoBanner);
